import React, { useContext } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalHeader, ModalBody, ModalFooter, Button } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import UserContext from '../../../contexts/UserContext';
import { UPDATE_ACCESS_POINT_STATUS, UPDATE_USER } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';

const ChangeAccessPointStatusSchema = Yup.object().shape({
  status: Yup.string().required('Du musst einen Status auswählen'),
  accessPointID: Yup.string().when('status', {
    is: 'CONFIRMED',
    then: Yup.string().required('Du musst eine Access Point ID eingeben'),
    otherwise: Yup.string().notRequired().nullable(),
  }),
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

const ChangeAccessPointStatusModal = ({ isOpen, onClose, refetch, accessPointID, dropPointId, identificationSuccess, status }) => {
  const [updateAccessPointStatus] = useMutation(UPDATE_ACCESS_POINT_STATUS);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Access Point Status ändern</ModalHeader>

        <Formik
          initialValues={{
            status: '',
            accessPointID,
            comment: '',
          }}
          validationSchema={ChangeAccessPointStatusSchema}
          onSubmit={async ({ status: newStatus, comment, accessPointID }) => {
            try {
              await updateAccessPointStatus({
                variables: {
                  internalUserId: user.id,
                  dropPointId,
                  accessPointID,
                  status: newStatus,
                  comment,
                },
              });
              await refetch();
              toast({
                title: 'Erfolg!',
                description: 'Der Status wurde erfolgreich geändert',
                status: 'success',
              });
            } catch (error) {
              console.log(error);
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            }
            onClose();
          }}>
          {({ values, errors, handleSubmit }) => {
            return (
              <Form>
                <ModalBody>
                  <Select
                    name="status"
                    label="Status"
                    options={[
                      {
                        label: 'Status auswählen',
                        value: '',
                        disabled: true,
                      },
                      {
                        label: 'Auf Warteliste',
                        value: 'WAITLIST',
                        disabled: status === 'WAITLIST' || status === 'CONFIRMED' || status === 'ACTIVE' || status === 'INACTIVE',
                      },
                      {
                        label: 'Genehmigt',
                        value: 'CONFIRMED',
                        disabled: status === 'CONFIRMED' || status === 'ACTIVE' || status === 'INACTIVE' || !identificationSuccess,
                      },
                      { label: 'Nicht freigeschaltet', value: 'REJECTED', disabled: status === 'REJECTED' },
                    ]}
                  />
                  {values.status === 'CONFIRMED' && <Input name="accessPointID" label="Access Point ID" disabled={!!accessPointID} />}

                  <Input name="comment" label="Kommentar/Begründung" />
                </ModalBody>

                <ModalFooter>
                  <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                    Schließen
                  </Button>
                  <Button ml="4" size="sm" colorScheme="brand" type="submit">
                    Speichern
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default ChangeAccessPointStatusModal;
